import { useEffect, useState } from 'react'
import { Printer } from 'lucide-react'
import { desktop } from '@/lib/printTransport'
import { getReceiptPrinter, setReceiptPrinter } from '@/lib/receiptPrinter'

/**
 * Picks the physical printer for customer receipts / Z-reports / credit notes /
 * PRA invoices (the "counter" printer). Machine-local, like the kitchen-station
 * printer mapping — printers are physical to this terminal. Empty => the
 * machine's OS default printer (the historical behaviour).
 *
 * Renders only inside the Bhookly desktop app; in a normal browser the receipt
 * always follows the OS default printer / print dialog, so there's nothing to
 * configure.
 */
export function ReceiptPrinterSetting() {
  const isDesktop = !!desktop()
  const [printers, setPrinters] = useState<{ name: string; isDefault: boolean }[] | null>(null)
  const [value, setValue] = useState<string>('')

  useEffect(() => {
    if (!isDesktop) return
    setValue(getReceiptPrinter())
    const d = desktop()
    if (!d) return
    let alive = true
    d.getPrinters()
      .then((list) => alive && setPrinters(list))
      .catch(() => alive && setPrinters([]))
    return () => {
      alive = false
    }
  }, [isDesktop])

  if (!isDesktop) return null

  const onChange = (v: string) => {
    setValue(v)
    setReceiptPrinter(v)
  }

  return (
    <div className="rounded-lg border border-border p-4">
      <label className="text-sm font-medium text-foreground flex items-center gap-2 mb-1">
        <Printer className="w-4 h-4" /> Receipt / counter printer
      </label>
      <p className="text-xs text-muted-foreground mb-2">
        Where customer receipts, Z-reports, credit notes and tax invoices print on this terminal.
        Leave as <strong>Default printer</strong> to use the computer&apos;s default.
      </p>
      {printers && printers.length > 0 ? (
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
        >
          <option value="">Default printer</option>
          {printers.map((p) => (
            <option key={p.name} value={p.name}>
              {p.name}
              {p.isDefault ? ' (default)' : ''}
            </option>
          ))}
        </select>
      ) : (
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Default printer"
          className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
        />
      )}
    </div>
  )
}
